import { type CSSProperties } from 'typestyle/lib/types';
import { cssRaw, style } from 'typestyle';
import DashboardTheme from '../dashboard-theme';

export const selectorRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 20,
};

export const selectorLabel: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: DashboardTheme.navy800,
};

export const officeSelect: CSSProperties = {
  padding: 8,
  paddingLeft: 16,
  paddingRight: 16,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  height: 36,
  minWidth: 220,
  backgroundColor: DashboardTheme.white,
};

export const selectPrompt: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: DashboardTheme.gray500,
  fontSize: 13.5,
};

export const detailGrid = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 16,
  marginBottom: 20,
});

export const officeGaugeCard: CSSProperties = {
  padding: '18px 20px',
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: DashboardTheme.radius,
  boxShadow: DashboardTheme.shadow,
  flex: 1,
  minWidth: 240,
};

export const lunchCard: CSSProperties = {
  padding: '18px 20px',
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: DashboardTheme.radius,
  boxShadow: DashboardTheme.shadow,
  flex: 1,
  minWidth: 200,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
};

export const lunchValue: CSSProperties = {
  fontSize: 40,
  fontWeight: 700,
  fontFamily: DashboardTheme.headerFontFamily,
  color: DashboardTheme.navy900,
};

export const lunchDescription: CSSProperties = {
  fontSize: 12.5,
  color: DashboardTheme.gray700,
  marginTop: 6,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const sectionCard: CSSProperties = {
  padding: '18px 20px',
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: DashboardTheme.radius,
  boxShadow: DashboardTheme.shadow,
  marginBottom: 20,
};

export const sectionTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: DashboardTheme.navy800,
  marginBottom: 12,
};

// A stable class (not just a style object) so the row-hover rule below can be scoped to it.
export const tableWrap = style({
  overflowX: 'auto',
  minWidth: 0,
  borderRadius: 12,
  border: `1px solid ${DashboardTheme.gray200}`,
});

cssRaw(
  `
  .${tableWrap} tbody tr {
    transition: background-color .12s ease;
  }
  .${tableWrap} tbody tr:hover {
    background-color: ${DashboardTheme.gray50};
  }
  `,
);

export const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

export const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: DashboardTheme.gray700,
  background: DashboardTheme.gray50,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  padding: '10px 12px',
};

export const td: CSSProperties = {
  fontSize: 13,
  color: DashboardTheme.navy800,
  borderBottom: `1px solid ${DashboardTheme.gray100}`,
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const barTrack: CSSProperties = {
  position: 'relative',
  width: 100,
  height: 8,
  borderRadius: 4,
  background: DashboardTheme.gray100,
  overflow: 'hidden',
};

export const barFill = (percentage: number, color: string): CSSProperties => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: `${Math.min(Math.max(percentage, 0), 100)}%`,
  background: color,
  borderRadius: 4,
});

export const barCell: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

export const barLabel: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray700,
  minWidth: 34,
  fontFamily: DashboardTheme.monoFontFamily,
};

export const customBadge: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 999,
  marginLeft: 6,
  background: DashboardTheme.amber100,
  color: DashboardTheme.amber600,
};

export const emptyMessage: CSSProperties = {
  color: DashboardTheme.gray500,
  fontSize: 13.5,
};
